/**
 * ContactConflictBadges — conflict indicator + one-click resolver for scalar
 * contact fields whose value diverges across provider accounts (§3.6, issue #4007).
 * The backend writer never silently last-writer-wins: it raises
 * `fieldProvenance[field].conflicted` and records each observed value with the
 * source(s) that vouch for it. This surfaces that stored divergence in the contact
 * edit header as a small "⚑ conflict" chip per conflicted field, listing each
 * candidate value with per-value provider attribution (reusing ProvenanceIcons,
 * §3.5).
 *
 * Each candidate value is a clickable "Use this value" button: on click the
 * resolver (`useResolveConflict`) does an ETag read-modify-write PATCH that writes
 * the chosen scalar AND clears `fieldProvenance[field].conflicted`, then invalidates
 * the contact list so the chip disappears (AC#2). The chosen value is pinned
 * locally-dirty, so the backend writer never re-flags it on the next sync (AC#3).
 *
 * Self-contained like ProvenanceIcons: it reads the contact's own
 * `id`/`_etag`/`fieldProvenance` (lifted to top level by transformObject) and owns
 * the resolver hook, so the call site only drops it in. Renders nothing for
 * native/legacy contacts (no fieldProvenance → conflictedFields returns []), so
 * it's safe to drop in anywhere.
 */
import type { ConflictedField } from '../contactProvenance';
import { conflictedFields } from '../contactProvenance';
import { useResolveConflict, type ResolvableContact } from '../hooks/useResolveConflict';
import { ProvenanceIcons } from './ProvenanceIcons';

interface ConflictedContact extends Partial<ResolvableContact> {
  attributes?: Record<string, unknown> | null;
}

function fieldLabel(field: string): string {
  // Humanize camelCase field keys for the tooltip (jobTitle → "job title").
  return field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').toLowerCase();
}

export function ContactConflictBadges({ contact, size = 12 }: { contact: ConflictedContact | null | undefined; size?: number }) {
  // Wrapper: no conflicts → render nothing AND call NO resolver hook, so
  // native/legacy contacts (and the many ContactEditModal tests that mount this via
  // ContactEditHeaderBar without a QueryClientProvider) never touch useUpdateObject.
  // Only a contact that actually carries a conflict mounts the inner component that
  // owns the mutation — same wrapper discipline as ProvenanceIcons.
  const conflicts = conflictedFields(contact as Parameters<typeof conflictedFields>[0]);
  if (conflicts.length === 0) return null;
  return <ContactConflictBadgesInner conflicts={conflicts} contact={contact} size={size} />;
}

function ContactConflictBadgesInner({
  conflicts,
  contact,
  size,
}: {
  conflicts: ConflictedField[];
  contact: ConflictedContact | null | undefined;
  size: number;
}) {
  const { resolveConflict, isResolving } = useResolveConflict();
  // Only contacts with an id can be resolved (always true for a persisted contact);
  // without one the chip stays read-only rather than firing a write that 404s.
  const onResolve = contact?.id
    ? (field: string, value: string) => resolveConflict(contact as ResolvableContact, field, value)
    : undefined;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {conflicts.map((c) => (
        <ConflictChip key={c.field} conflict={c} size={size} onResolve={onResolve} isResolving={isResolving} />
      ))}
    </span>
  );
}

function ConflictChip({
  conflict,
  size,
  onResolve,
  isResolving,
}: {
  conflict: ConflictedField;
  size: number;
  onResolve?: (field: string, value: string) => void;
  isResolving: boolean;
}) {
  // Plain-text tooltip on the chip lists the field + each candidate value so the
  // divergence is legible even where the inline ProvenanceIcons can't render.
  const tooltip = `Conflicting ${fieldLabel(conflict.field)}: ${conflict.candidates.map((c) => c.value).join(' vs ')}`;
  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        fontWeight: 600,
        color: '#92400E',
        background: '#FEF3C7',
        border: '1px solid #FCD34D',
        borderRadius: 12,
        padding: '1px 8px',
        lineHeight: 1.4,
      }}
    >
      <span aria-hidden>⚑</span>
      <span>{fieldLabel(conflict.field)} conflict</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--color-text-faint)', fontWeight: 400 }}>
        {conflict.candidates.map((c, i) => (
          <button
            key={`${c.value}-${i}`}
            type="button"
            // "Use this value" — resolve the conflict to this candidate. Disabled
            // while a resolve is in flight (or when the contact has no id to write to).
            onClick={onResolve ? () => onResolve(conflict.field, c.value) : undefined}
            disabled={!onResolve || isResolving}
            title={onResolve ? `Use "${c.value}" for ${fieldLabel(conflict.field)}` : undefined}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: '0 4px',
              fontSize: 11,
              fontWeight: 400,
              fontFamily: 'inherit',
              lineHeight: 1.4,
              color: 'var(--color-text-faint)',
              background: 'transparent',
              border: '1px solid transparent',
              borderRadius: 8,
              cursor: onResolve && !isResolving ? 'pointer' : 'default',
            }}
          >
            <span>{c.value}</span>
            <ProvenanceIcons sources={c.sources} size={size} />
          </button>
        ))}
      </span>
    </span>
  );
}
